import { useState, useCallback, useEffect } from 'react';
import {
  Form,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Select,
  Alert,
  Box,
  Toggle,
  SelectProps,
} from '@cloudscape-design/components';
import useUserProfile from 'src/hooks/useUserProfile';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { REGION_OPTIONS, getCountriesByRegion } from 'src/contstant/commonLabels';
import {
  Location,
  UpdateLocationCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import { useMutation } from '@tanstack/react-query';
import { locationService } from 'src/data/api/helios-service-ts/locationService';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Persona } from 'src/contstant/Persona';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
interface LocationDetailsFormProps {
  location: Location;
  onViewDismiss: () => void;
}
type LocationParams = Pick<
  UpdateLocationCommandInput,
  'businessUnitId' | 'locationCode' | 'isEnabledForBU' | 'region' | 'country'
>;

const locationFormSchema = z.object({
  businessUnitId: z.string(),
  locationCode: z.string(),
  isEnabledForBU: z.boolean(),
  region: z.string({
    error: 'Region is required',
  }),
  country: z
    .string({
      error: 'Country is required',
    })
    .min(1, {
      message: 'Country is required',
    }),
}) satisfies z.ZodType<LocationParams>;
export function LocationDetailsFormUpdate({
  location,
  onViewDismiss,
}: LocationDetailsFormProps) {
  const handleCloseLocationForm = () => {
    onViewDismiss();
  };
  const [availableCountries, setAvailableCountries] = useState<SelectProps.Option[]>(
    getCountriesByRegion(location.region),
  );
  const { getUserId, getPersona, getUserProgramId, getUserBusinessUnitId, getDivisions } =
    useUserProfile();

  const { userPermission, checkUserPermissions } = useUsers();
  const form = useForm({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      businessUnitId: location?.businessUnitId || '',
      locationCode: location?.locationCode || '',
      isEnabledForBU: location?.isEnabledForBU ?? true,
      region: location?.region || '',
      country: location?.country || '',
    },
  });
  const isEditLocation = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionConfig.LOCATION.EDIT_LOCATION,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions, getPersona]);
  useEffect(() => {
    if (getPersona() != Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [permissionAccessConfig.LOCATION.EDIT_LOCATION],
        divisions: getDivisions(),
      });
    }
  }, [
    getPersona(),
    getUserProgramId(),
    getUserBusinessUnitId(),
    getDivisions(),
    getUserId(),
  ]);
  const {
    mutate: updateLocationMutation,
    status: updateLocationStatus,
    error: updateLocationError,
  } = useMutation({
    mutationFn: (data: UpdateLocationCommandInput) => {
      return locationService.updateLocation({
        ...data,
        businessUnitId: location.businessUnitId,
        reason: 'Location details updated',
      });
    },
    onSuccess() {
      handleCloseLocationForm();
    },
  });
  if (!isEditLocation()) {
    return (
      <Box margin={{ top: 'xl' }}>
        <Alert type="info">You are not authorized to update a location.</Alert>
      </Box>
    );
  }
  return (
    <form
      onSubmit={form.handleSubmit((data) =>
        updateLocationMutation({
          ...data,
          lastUpdatedBy: getUserId(),
        }),
      )}
      id="location-update-form"
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="cancel-button"
              formAction="none"
              variant="link"
              onClick={handleCloseLocationForm}
              disabled={updateLocationStatus === 'pending'}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              form="location-update-form"
              formAction="submit"
              loading={updateLocationStatus === 'pending'}
              // disabled if the data in the form hasn't change or if loading
              disabled={updateLocationStatus === 'pending' || !form.formState.isDirty}
            >
              Save
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <Controller
            name="locationCode"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="locationCode-field"
                label={<RequiredFieldLabel label="Location" />}
                errorText={form.formState.errors.locationCode?.message}
              >
                <Input
                  readOnly={!!location} // Location code is read-only when editing
                  data-testid="locationCode-input"
                  {...field}
                  value={field.value}
                  onChange={({ detail }) => field.onChange(detail.value)}
                />
              </FormField>
            )}
          />
          <Controller
            name="businessUnitId"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="businessUnitId-field"
                label={<RequiredFieldLabel label="Business Unit" />}
                errorText={form.formState.errors.businessUnitId?.message}
              >
                <Input data-testid="businessUnit-input" value={field.value} readOnly />
              </FormField>
            )}
          />
          <Controller
            name="region"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="region-field"
                label={<RequiredFieldLabel label="Region" />}
                errorText={form.formState.errors.region?.message}
              >
                <Select
                  data-testid="region-select"
                  options={REGION_OPTIONS}
                  selectedOption={
                    REGION_OPTIONS.find((option) => option.value === field.value) || null
                  }
                  readOnly={!isEditLocation()}
                  placeholder="Choose a Region"
                  filteringType="auto"
                  onChange={({ detail }) => {
                    const selectedRegion = detail.selectedOption.value || '';
                    setAvailableCountries(getCountriesByRegion(selectedRegion));
                    form.setValue('country', '');
                    field.onChange(selectedRegion);
                  }}
                />
              </FormField>
            )}
          />
          <Controller
            name="country"
            control={form.control}
            render={({ field }) => (
              <FormField
                data-testid="country-field"
                label={<RequiredFieldLabel label="Country" />}
                errorText={form.formState.errors.country?.message}
              >
                <Select
                  data-testid="country-select"
                  options={availableCountries}
                  selectedOption={
                    availableCountries.find((option) => option.value === field.value) ||
                    null
                  }
                  readOnly={!isEditLocation()}
                  placeholder="Choose a Country"
                  filteringType="auto"
                  onChange={({ detail }) =>
                    field.onChange(detail.selectedOption.value || '')
                  }
                />
              </FormField>
            )}
          />
          <Controller
            name="isEnabledForBU"
            control={form.control}
            render={({ field }) => (
              <FormField
                label="Status"
                constraintText="Enable or disable the location using this switch."
                errorText={form.formState.errors.isEnabledForBU?.message}
              >
                <Toggle
                  data-testid="enabled-toggle"
                  checked={field.value}
                  readOnly={!isEditLocation()}
                  onChange={({ detail }) => field.onChange(detail.checked)}
                >
                  {field.value ? 'Active' : 'Inactive'}
                </Toggle>
              </FormField>
            )}
          />
        </SpaceBetween>
      </Form>

      {updateLocationStatus === 'error' ? (
        <Box margin={{ top: 'xl' }}>
          <Alert type="error">
            {updateLocationError?.message ??
              `An error occurred while updating location ${location.locationCode}`}
          </Alert>
        </Box>
      ) : null}
    </form>
  );
}
